import { graphql, Link } from 'gatsby';
import Image from 'gatsby-image';
import * as React from 'react';
import { Button } from '../components/button';
import { Dialog } from '../components/dialog';
import { SurveyIcon } from '../components/icon/survey-icon';
import { CheckedItem } from '../components/list';
import { useLocalize } from '../components/localize-context';
import { OfferBox } from '../components/offer-box';
import { PriceDisplay } from '../components/price-display';
import { SamePageLink } from '../components/same-page-link';
import { Seo } from '../components/seo';
import { ServiceBookingForm } from '../components/service-booking-form';
import { TitleWithBadge } from '../components/title-with-badge';
import { VisitorContext } from '../components/visitor-context';
import { VisitorTypeSwitcher } from '../components/visitor-type-switcher';
import { trackEvent } from '../analytics';

export default function Package({ data: { allPrismicPackage, schedules } }) {
  const localize = useLocalize();
  const [visitorType] = React.useContext(VisitorContext);
  return (
    <>
      <Seo title={localize('Packages')} />
      <div className="max-w-5xl py-6 px-4 mx-auto">
        <h1 className="text-3xl">{localize('Packages')}</h1>
        <div className="pb-6">
          <VisitorTypeSwitcher />
        </div>
        <div className="md:flex md:shadow">
          {allPrismicPackage.nodes.map(({ data, uid }) => {
            const adultPrice =
              visitorType === 'local'
                ? data.localAdultPrice
                : data.foreignAdultPrice;

            const kidPrice =
              visitorType === 'local'
                ? data.localKidPrice
                : data.foreignKidPrice;

            return (
              <OfferBox
                title={data.name.text}
                className="flex-1 mb-4 md:mb-0 mx-auto shadow md:shadow-none"
                key={uid}
              >
                <div className="text-center">
                  {adultPrice === kidPrice ? (
                    <PriceDisplay type="both" price={adultPrice} />
                  ) : (
                    <>
                      <PriceDisplay
                        type="adult"
                        price={adultPrice}
                        className="mr-4 mb-4"
                      />
                      <PriceDisplay type="kid" price={kidPrice} />
                    </>
                  )}
                </div>
                <div className="text-center flex-1">
                  <div className="my-6">
                    <ul className="inline-block text-gray-600">
                      {data.included_services.map(({ service }) => (
                        <CheckedItem key={service.id}>
                          {service.document[0].data.name.text}
                        </CheckedItem>
                      ))}
                      {data.additionalServices.map((service, index) =>
                        service.text.text ? (
                          <CheckedItem key={index}>
                            {service.text.text}
                          </CheckedItem>
                        ) : null
                      )}
                    </ul>
                  </div>
                </div>
                <div className="mb-6 text-center">
                  <Button
                    variant="bordered"
                    renderContainer={({ className }) => (
                      <SamePageLink
                        onClick={() => {
                          trackEvent({
                            category: 'button',
                            action: 'click',
                            label: `scroll to package ${uid}`,
                          });
                        }}
                        targetId={uid}
                        className={className}
                      >
                        {localize('Learn More')}
                      </SamePageLink>
                    )}
                  />
                </div>
              </OfferBox>
            );
          })}
        </div>
      </div>
      {allPrismicPackage.nodes.map(({ data, uid }) => (
        <PackageDetails
          data={data}
          uid={uid}
          schedules={schedules.nodes}
          key={uid}
        />
      ))}
    </>
  );
}

export const pageQuery = graphql`
  query PackageQuery($lang: String!) {
    allPrismicPackage(
      filter: { lang: { eq: $lang } }
      sort: { fields: data___order }
    ) {
      nodes {
        uid
        data {
          name {
            text
          }
          itinerary {
            url
          }
          localAdultPrice: unit_price__local_adult_
          localKidPrice: unit_price__local_kid_
          foreignAdultPrice: unit_price__international_adult_
          foreignKidPrice: unit_price__international_kid_
          included_services {
            service {
              id
              slug
              document {
                data {
                  name {
                    text
                  }
                }
              }
            }
          }
          additionalServices: additional_services {
            text {
              text
            }
          }
          description {
            html
          }
          previews {
            images {
              alt
              url
              localFile {
                childImageSharp {
                  fluid(maxWidth: 500, quality: 100) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
            }
          }
        }
      }
    }
    schedules: allScheduleYaml(filter: { isFuture: { eq: true } }) {
      nodes {
        date(formatString: "YYYY-MM-DD")
        time
      }
    }
  }
`;

function PackageDetails({ data, uid, schedules }) {
  const localize = useLocalize();
  const [showDialog, setShowDialog] = React.useState(false);
  const [visitorType] = React.useContext(VisitorContext);

  const images = data.previews
    .filter(preview => !!preview.images.url)
    .map(preview => preview.images);

  return (
    <div className="container p-4 mx-auto my-8">
      <section className="max-w-3xl mx-auto" id={uid}>
        <TitleWithBadge className="text-4xl mb-4">
          {data.name.text}
        </TitleWithBadge>
        {images.length > 0 && (
          <div className="sm:flex">
            {images.map(image => (
              <Image
                fluid={image.localFile.childImageSharp.fluid}
                className="mb-2 mr-1 flex-1 rounded-lg"
                key={image.url}
              />
            ))}
          </div>
        )}
        <div className="pt-4">
          <div
            className="rich-text"
            dangerouslySetInnerHTML={{
              __html: data.description.html,
            }}
          />
        </div>
        <div>
          <Button
            onClick={() => setShowDialog(true)}
            variant="primary"
            className="w-32 text-2xl mr-4"
          >
            {localize('Book')}
          </Button>
          {data.itinerary && (
            <Button
              className="w-32 text-2xl"
              variant="white"
              renderContainer={({ className }) => (
                <Link to={data.itinerary.url} className={className}>
                  {localize('Itinerary')}{' '}
                  <SurveyIcon className="ml-2" width={36} />
                </Link>
              )}
            />
          )}
        </div>
      </section>
      <Dialog
        isOpen={showDialog}
        onDismiss={() => setShowDialog(false)}
        title={`${localize('Book')} ${data.name.text}`}
      >
        <ServiceBookingForm
          productName={data.name.text}
          schedules={schedules}
          adultPrice={
            visitorType === 'local'
              ? data.localAdultPrice
              : data.foreignAdultPrice
          }
          kidPrice={
            visitorType === 'local' ? data.localKidPrice : data.foreignKidPrice
          }
        />
      </Dialog>
    </div>
  );
}
